import { Grid, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AxiosResponse } from 'axios';
import {
  IndexedStrings,
  SelectableTableColumn,
  UpHeaderColumn,
  inputTableBorder
} from '../../../../components/SelectableTable/SelectableTable.types';
import { useLayoutContext } from '../../../../Context/Layout.context';
import usePermission, { UserPermission } from '../../../../hooks/usePermission';
import { ActionMenuItem } from '../../../../components/ActionsMenu/ActionsMenu.types';
import { SelectableTable } from '../../../../components/SelectableTable/SelectableTable.component';
import { useStyles } from '../ExaminerBlockingLimits/ExaminerIndividualBlockingLimits.styles';
import {
  ExaminerIndividualBlockingLimits,
  ExaminerIndividualBlockingLimitsFilter,
  ExaminerIndividualBlockingLimitsFilterElements,
  ExaminerIndividualBlockingLimitsResponse,
  ExaminerIndividualBlockingLimitsSortById,
  ExaminerIndividualBlockingLimitsTableItem,
  LimitType,
} from './ExaminerIndividualBlockingLimits.types';
import ActionsMenu from '../../../../components/ActionsMenu/ActionsMenu.component';
import { ItemsFilter } from '../../../../components/SelectableTable/filters/ItemsFilter.component';
import { HorizonalTabs } from '../../../../components/Tabs/HorizontalTabs/HorizonalTabs.component';
import { EditExaminerIndividualBlockingLimits } from './EditExaminerIndividualBlockingLimits.component';
import FilterStateGroup, { LabelWithValue } from '../../../../models/filterStateGroup.types';
import {
  deleteExaminerIndividualLimits,
  getExaminersBlockingLimits,
} from '../../../../services/exams/examSheet/examSheet.service';
import Pagination from '../../../../components/Pagination.component';
import { defaultPageInfo, IPageInfo } from '../../../../models/page.types';
import { Prompt } from '../../../../components/Prompt/Prompt.component';
import { InactiveProcessBadge } from '../InactiveProcessBadge/InactiveProcessBadge.component';
import { AssessmentSessionProcessTypeId } from '../../../../services/exams/assessmentSession/assessments.types';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

interface ExamTaskGroup {
  id: string;
  name: string;
  isControlProcessRequired: boolean;
}

interface ExaminerIndividualBlockingLimitsTableProps {
  onClose: () => void;
  examTaskGroups: ExamTaskGroup[];
  isUserKKOrKGZ: boolean;
}



const defaultFilter = {
  name: '',
  examinerNumber: '',
  limits: [],
};

export const ExaminerIndividualBlockingLimitsTable: React.FC<ExaminerIndividualBlockingLimitsTableProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { showLoader, openAlert } = useLayoutContext();

  const [sortByColumnId, setSortByColumnId] = useState<ExaminerIndividualBlockingLimitsSortById>('name');
  const [isDescending, setIsDescending] = useState<boolean>(false);
  const [filter, setFilter] = useState<ExaminerIndividualBlockingLimitsFilter>(defaultFilter);
  const [isValidFilter, setIsValidFilter] = useState<boolean>(true);
  const [filterState, setFilterState] = useState<FilterStateGroup<ExaminerIndividualBlockingLimitsFilter>[]>([]);
  const [pageInfo, setPageInfo] = useState<IPageInfo>(defaultPageInfo);

  const [individualLimits, setIndividualLimits] = useState<ExaminerIndividualBlockingLimitsTableItem[]>([]);
  const [isEditindIvidualLimitsModalOpen, setIsEditindIvidualLimitsModalOpen] = useState<boolean>(false);
  const [selectedIndividualBlockingLimits, setSelectedIndividualBlockingLimits] =
    useState<ExaminerIndividualBlockingLimits>();
  const [
    isDeleteIndividualExaminerLimitsConfirmationModalOpen,
    setIsDeleteExaminerIndividualLimitsConfirmationModalOpen,
  ] = useState<boolean>(false);

  const [examTaskGroupIndex, setExamTaskGroupIndex] = useState<number>(0);
  const [selectedExamTaskGroup, setselectedExamTaskGroup] = useState<ExamTaskGroup>(props.examTaskGroups[0]);

  const hasAdminSessionStatusManage = usePermission(UserPermission.AdminSessionStatusManage);

  const columns: SelectableTableColumn[] = [
    {
      id: 'inactiveProcessBadge',
      type: 'children',
      title: '',
      filterType: null,
      sortable: false,
    },
    {
      id: 'name',
      type: 'text',
      title: t('sessionsParameters:examinerIndividualBlockingLimits.table.name'),
      filterType: 'search',
      sortable: true,
    },
    {
      id: 'examinerNumber',
      type: 'text',
      title: t('sessionsParameters:examinerIndividualBlockingLimits.table.examinerNumber'),
      filterType: 'search',
      sortable: true,
    },
    {
      id: 'limit',
      type: 'text',
      title: t('sessionsParameters:examinerIndividualBlockingLimits.table.limit'),
      filterType: 'filter',
      sortable: true,
    },
    {
      id: 'totalLimitOutOfTolerance',
      type: 'text',
      title: t('sessionsParameters:examinerIndividualBlockingLimits.table.totalLimitOutOfTolerance'),
      filterType: null,
      style: { borderLeft: inputTableBorder }
    },
    {
      id: 'sequentialLimitOutOfTolerance',
      type: 'text',
      title: t('sessionsParameters:examinerIndividualBlockingLimits.table.sequentialLimitOutOfTolerance'),
      filterType: null,
      style: { borderRight: inputTableBorder}
    },
    {
      id: 'totalLimitWithinTolerance',
      type: 'text',
      title: t('sessionsParameters:examinerIndividualBlockingLimits.table.totalLimitWithinTolerance'),
      filterType: null,
    },
    {
      id: 'sequentialLimitWithinTolerance',
      type: 'text',
      title: t('sessionsParameters:examinerIndividualBlockingLimits.table.sequentialLimitWithinTolerance'),
      filterType: null,
      style: { borderRight: inputTableBorder}
    },
    {
      id: 'actions',
      type: 'actions',
      title: 'Akcje',
      filterType: null,
      skipEnterMethod: true,
    },
  ];
  
  const upHeaderColumns: UpHeaderColumn[] = [
    {
      id: 'emptyHeaderColumns1',
      colSpan: 4,
      style: { borderBottom: 'none' }
    },
    {
      id: 'limitOutOfTolerance',
      title: t('sessionsParameters:examinerIndividualBlockingLimits.table.limitOutOfTolerance'),
      colSpan: 2,
      centered: true,
      style: { borderLeft: inputTableBorder, borderRight: inputTableBorder, borderBottom: 'none' }
    },
    {
      id: 'limitWithinTolerance',
      title: t('sessionsParameters:examinerIndividualBlockingLimits.table.limitWithinTolerance'),
      colSpan: 2,
      centered: true,
      style: { borderRight: inputTableBorder, borderBottom: 'none' }
    },
    {
      id: 'emptyHeaderColumns2',
      colSpan: 2,
      style: { borderBottom: 'none' }
    },
  ];

  const handleOnClick = (index: number): void => {
    setExamTaskGroupIndex(index);
    setselectedExamTaskGroup(props.examTaskGroups[index]);
  };

  const getActionsMenuIcon = (row: ExaminerIndividualBlockingLimits) => {
    const items: ActionMenuItem[] = [];
    if (hasAdminSessionStatusManage || props.isUserKKOrKGZ) {
      if (row.blockingLimitsType === LimitType.globalLimit) {
        items.push({
          text: 'Zdefiniuj limity indywidualne',
          onClick: () => {
            setSelectedIndividualBlockingLimits(row);
            setIsEditindIvidualLimitsModalOpen(true);
          },
        });
      } else {
        items.push({
          text: 'Edytuj',
          onClick: () => {
            setSelectedIndividualBlockingLimits(row);
            setIsEditindIvidualLimitsModalOpen(true);
          },
        });
        items.push({
          text: 'Usuń',
          onClick: () => {
            setSelectedIndividualBlockingLimits(row);
            setIsDeleteExaminerIndividualLimitsConfirmationModalOpen(true);
          },
        });
      }
    }
    return <ActionsMenu menuItems={items} />;
  };

  const getLimitTypeName = (limit: LimitType) => {
    switch (limit) {
      case LimitType.globalLimit:
        return 'Globalny';
      case LimitType.individualLimit:
        return 'Indywidualny';
    }
  };

  const refreshData = _.debounce((filter: ExaminerIndividualBlockingLimitsFilter, signal?: AbortSignal) => {
    if (!isValidFilter) return;
    showLoader(true);
    getExaminersBlockingLimits(
      selectedExamTaskGroup.id,
      filter,
      sortByColumnId,
      isDescending,
      pageInfo.currentPage,
      pageInfo.pageSize,
      signal,
    )
      .then((response: AxiosResponse<ExaminerIndividualBlockingLimitsResponse>) => {
        if (response.data.examinerBlockingLimits) {
          if (selectedExamTaskGroup.isControlProcessRequired) {
            const tableData: ExaminerIndividualBlockingLimitsTableItem[] = response.data.examinerBlockingLimits.map(
              (row: ExaminerIndividualBlockingLimits) => ({
                name: `${row.lastName} ${row.firstName}`,
                examinerPermissionId: row.examinerPermissionId,
                examinerNumber: row.examinerNumber,
                limit: getLimitTypeName(row.blockingLimitsType),
                totalLimitOutOfTolerance: row.totalLimitOutOfTolerance || '-',
                sequentialLimitOutOfTolerance: row.sequentialLimitOutOfTolerance || '-',
                totalLimitWithinTolerance: row.totalLimitWithinTolerance || '-',
                sequentialLimitWithinTolerance: row.sequentialLimitWithinTolerance || '-',
                actions: getActionsMenuIcon(row),
              }),
            );
            setIndividualLimits(tableData);
          } else {
            const tableData = [
              {
                name: '',
                examinerPermissionId: '',
                examinerNumber: '',
                limit: '',
                totalLimitOutOfTolerance: '',
                sequentialLimitOutOfTolerance: '',
                totalLimitWithinTolerance: '',
                sequentialLimitWithinTolerance: '',
                actions: <></>,
                inactiveProcessBadge: {
                  colSpan: 9,
                  data: (
                    <>
                      <InactiveProcessBadge processType={AssessmentSessionProcessTypeId.Control} />
                    </>
                  ),
                },
              },
            ];
            setIndividualLimits(tableData);
          }
        }
        if (response.data.page) {
          setPageInfo(response.data.page);
        }
      })
      .finally(() => {
        showLoader(false);
      });
  }, 1000);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    refreshData(filter, signal);
  }, [filter, isDescending, sortByColumnId, selectedExamTaskGroup, pageInfo.currentPage, pageInfo.pageSize]);

  const handleFilterChange = useCallback(
    (name: string, value: unknown) => {
      typeof value === 'string' && value.length === 1 ? setIsValidFilter(false) : setIsValidFilter(true);
      const newFilterAttribute: Partial<ExaminerIndividualBlockingLimitsFilter> = {
        [name]: Array.isArray(value) ? value : [value],
      };
      setFilter((prevState) => {
        return {
          ...prevState,
          ...newFilterAttribute,
        };
      });
    },
    [setFilter],
  );

  useEffect(() => {
    const state: FilterStateGroup<ExaminerIndividualBlockingLimitsFilter>[] = [];
    if (filter.limits.length > 0) {
      state.push({
        name: 'Typ limitu:',
        propertyName: 'limits',
        checkedItems: filter.limits.map((limit) => {
          return {
            label: getLimitTypeName(limit),
            value: limit,
          };
        }),
      });
    }
    setFilterState(state);
  }, [filter]);

  const filterElements: ExaminerIndividualBlockingLimitsFilterElements = {
    limit: (
      <ItemsFilter<LimitType>
        valueName="limits"
        valueLabel="Typ limitu"
        onChange={handleFilterChange}
        resetFilter={false}
        itemsWithValues={[
          { label: 'Globalny', value: LimitType.globalLimit },
          { label: 'Indywidualny', value: LimitType.individualLimit },
        ]}
        filter={filter.limits}
      />
    ),
  };

  const uncheckFilterItem = (
    label?: string,
    propertyName?: keyof ExaminerIndividualBlockingLimitsFilter,
    dataItem?: LabelWithValue,
  ) => {
    if (propertyName) {
      setFilter((prevValue) => {
        return {
          ...prevValue,
          [propertyName]:
            Array.isArray(prevValue[propertyName]) && dataItem
              ? (prevValue[propertyName] as Array<unknown>).filter((item) => item !== dataItem.value)
              : '',
        };
      });
    }
  };

  const uncheckAllFilters = () => {
    setFilterState([]);
    setFilter(defaultFilter);
  };

  const filterTranslated: IndexedStrings = useMemo(() => {
    const result: IndexedStrings = {};
    Object.keys(filter).forEach((key: string) => {
      result[key] = filter[key as keyof ExaminerIndividualBlockingLimitsFilter]?.toString() ?? '';
    });
    return result;
  }, [filter]);

  const filterElementsTranslated: { [key: string]: JSX.Element } = {};
  Object.keys(filterElements).forEach((key: string) => {
    filterElementsTranslated[key] = filterElements[key as keyof ExaminerIndividualBlockingLimitsFilterElements];
  });

  const getPages = useCallback(
    (_page?: number, _newPageSize?: number) => {
      setPageInfo((state) => ({
        ...state,
        currentPage: _page ? _page : 0,
        pageSize: _newPageSize ? _newPageSize : state.pageSize,
      }));
    },
    [filter, pageInfo],
  );

  const handleOnClose = () => {
    setIsEditindIvidualLimitsModalOpen(false);
    refreshData(filter);
  };

  const handleOnCancel = () => {
    setIsEditindIvidualLimitsModalOpen(false);
  };

  const handleDeleteExaminerIndividualLimits = () => {
    if (selectedIndividualBlockingLimits?.examinerPermissionId) {
      deleteExaminerIndividualLimits(selectedIndividualBlockingLimits.examinerPermissionId)
        .then(
          () => {
            openAlert('success', 'Dane zostały pomyślnie usunięte');
            refreshData(filter);
          },
          () => {
            openAlert('error', 'Usunięcie danych nie powiodło się. Spróbuj ponowanie później');
          },
        )
        .finally(() => setIsDeleteExaminerIndividualLimitsConfirmationModalOpen(false));
    }
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} className={classes.header}>
          <Grid item xs={12}>
            <Typography className={classes.tabTitle}>{t('sessionsParameters:examinerIndividualBlockingLimits.header')}</Typography>
            <Pagination className={classes.pagination} pageInfo={pageInfo} getPage={getPages} />
          </Grid>
          {props.examTaskGroups.length > 1 && (
            <Grid item xs={12} className={classes.horizonalTabs}>
              <HorizonalTabs
                onClick={handleOnClick}
                selectedItemIndex={examTaskGroupIndex}
                items={props.examTaskGroups.map((e) => {
                  return { label: e.name };
                })}
              />
            </Grid>
          )}
        </Grid>
        <div style={{ height: '400px', overflowY: 'auto', width: '940px' }}>
          <SelectableTable<
            ExaminerIndividualBlockingLimitsTableItem,
            ExaminerIndividualBlockingLimitsSortById,
            ExaminerIndividualBlockingLimitsFilter
          >
            upHeaderColumns={upHeaderColumns}
            columns={columns}
            rows={individualLimits}
            onFilterChange={handleFilterChange}
            closeFilterItem={uncheckFilterItem}
            uncheckAllFilters={uncheckAllFilters}
            filter={filterTranslated}
            filterbar={filterState ?? []}
            filterElements={filterElementsTranslated}
            sortByProperty={sortByColumnId}
            isDescending={isDescending}
            setIsDescending={setIsDescending}
            caption={t('sessionsParameters:examinerIndividualBlockingLimits.header')}
          />
        </div>
      </Grid>
      {isEditindIvidualLimitsModalOpen && (
        <EditExaminerIndividualBlockingLimits
          limits={selectedIndividualBlockingLimits}
          onClose={handleOnClose}
          onCancel={handleOnCancel}
        />
      )}
      {isDeleteIndividualExaminerLimitsConfirmationModalOpen && (
        <Prompt
          type="confirmation"
          mainActionButtonText="TAK"
          open={true}
          mainText="Czy na pewno chcesz usunąć indywidualne limity blokujące ustawione dla egzaminatora?"
          onConfirm={() => handleDeleteExaminerIndividualLimits()}
          onCancel={() => setIsDeleteExaminerIndividualLimitsConfirmationModalOpen(false)}
        />
      )}
    </>
  );
};
